// ── Cotações Tab ──────────────────────────────────────────────────────────────
import { getTrip }          from '@/store/tripState';
import { openModal }        from '@/ui/modal';
import { COTACAO_CATS,
         COTACAO_CAT_LABELS,
         COTACAO_STATUS,
         TRANSPORT_TYPES }  from '@/constants';
import { toArr, fmt, fmtR, sanitize } from '@/helpers';
import type { Cotacao,
              CotacaoCat,
              CotacaoStatus } from '@/types';

const CAT_LABELS = COTACAO_CAT_LABELS;
const STATUS_BADGES: Record<string, string> = {
  confirmado:   '<span class="badge b-ok">Confirmado</span>',
  'em análise': '<span class="badge b-warn">Em análise</span>',
  descartado:   '<span class="badge b-danger">Descartado</span>',
};

let compareSelected = new Set<string>();

export function renderCotacoes(): void {
  const el     = document.getElementById('cotacoes-list');
  const search = ((document.getElementById('cotacoes-search') as HTMLInputElement)?.value ?? '').toLowerCase();
  const catF   = (document.getElementById('cotacoes-cat-filter') as HTMLSelectElement)?.value ?? '';
  if (!el) return;

  let arr = toArr(getTrip().cotacoes ?? {}) as Cotacao[];
  arr.sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR'));
  if (search) arr = arr.filter(c =>
    (c.nome ?? '').toLowerCase().includes(search) ||
    (CAT_LABELS[c.cat] ?? '').toLowerCase().includes(search));
  if (catF) arr = arr.filter(c => c.cat === catF);

  if (!arr.length) { el.innerHTML = '<div class="empty">Nenhuma cotação encontrada.</div>'; updateCompareBar(); return; }

  el.innerHTML = arr.map(c => {
    const sel = compareSelected.has(c.id);
    return `
      <div class="cotacao-card ${sel ? 'selected' : ''}" id="ccard-${c.id}">
        <button class="cotacao-select-btn ${sel ? 'on' : ''}" onclick="window.__toggleCompare('${c.id}')">${sel ? '✓' : ''}</button>
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;padding-right:28px">
          <div style="flex:1">
            <div style="display:flex;align-items:center;gap:7px;flex-wrap:wrap;margin-bottom:3px">
              <span class="card-title">${sanitize(c.nome ?? 'Sem nome')}</span>
              <span class="badge b-purple" style="font-size:10px">${CAT_LABELS[c.cat] ?? c.cat}</span>
              ${STATUS_BADGES[c.status as CotacaoStatus] ?? ''}
            </div>
            ${buildCotacaoSummary(c)}
            ${c.notas ? `<div style="font-size:11px;color:var(--hint);margin-top:4px">${sanitize(c.notas)}</div>` : ''}
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0">
            ${c.preco ? `<div style="font-size:15px;font-weight:500">${fmtR(c.preco)}</div>` : ''}
            <div style="display:flex;gap:3px">
              <button class="icon-btn" onclick="window.__openModal('cotacao','${c.id}')">✏</button>
              <button class="icon-btn del" onclick="window.__delItem('cotacoes','${c.id}')">×</button>
            </div>
          </div>
        </div>
      </div>`;
  }).join('');
  updateCompareBar();
}

function buildCotacaoSummary(c: Cotacao): string {
  const parts: string[] = [];
  if (c.cat === 'hotel') {
    if (c.cidade) parts.push(sanitize(c.cidade));
    if (c.checkin || c.checkout) parts.push(`${fmt(c.checkin ?? '')} → ${fmt(c.checkout ?? '')}${c.noites ? ' · ' + c.noites + ' noites' : ''}`);
    if (c.site) {
      const safeUrl = c.site.startsWith('http') ? c.site : 'https://' + c.site;
      parts.push(`<a href="${sanitize(safeUrl)}" target="_blank" style="color:var(--lux-blue)">${sanitize(c.site)}</a>`);
    }
    if (c.reserva) parts.push(`Reserva: ${sanitize(c.reserva)}`);
  }
  if (c.cat === 'transporte') {
    if (c.origem || c.destino) parts.push(`${sanitize(c.origem ?? '?')} → ${sanitize(c.destino ?? '?')}`);
    if (c.tipo) parts.push(c.tipo);
    const horaPartida = c.horaPartida || c.hora || '';
    const horarios = [
      horaPartida ? `Partida ${sanitize(horaPartida)}` : '',
      c.horaChegada ? `Chegada ${sanitize(c.horaChegada)}` : '',
    ].filter(Boolean).join(' → ');
    const datas = c.dataChegada
      ? `${c.data ? fmt(c.data) : '?'} → ${fmt(c.dataChegada)}`
      : c.data ? fmt(c.data) : '';
    if (datas || horarios) parts.push([datas, horarios].filter(Boolean).join(' · '));
    if (c.operadora || c.num) parts.push([c.operadora, c.num].filter(Boolean).map(s => sanitize(s as string)).join(' '));
  }
  if (c.cat === 'ingresso') {
    if (c.local) parts.push(`📍 ${sanitize(c.local)}`);
    if (c.date)  parts.push(fmt(c.date));
  }
  return parts.map(p => `<div class="card-sub">${p}</div>`).join('');
}

export function toggleCompare(id: string): void {
  if (compareSelected.has(id)) { compareSelected.delete(id); }
  else {
    if (compareSelected.size >= 3) { import('@/ui/toast').then(({ toastWarn }) => toastWarn('Selecione no máximo 3 itens.')); return; }
    compareSelected.add(id);
  }
  renderCotacoes();
}

export function clearCompare(): void { compareSelected.clear(); renderCotacoes(); }

function updateCompareBar(): void {
  const bar = document.getElementById('cotacoes-compare-bar');
  const lbl = document.getElementById('compare-bar-label');
  if (!bar) return;
  const n = compareSelected.size;
  bar.style.display = n >= 2 ? 'flex' : 'none';
  if (lbl) lbl.textContent = `${n} ite${n === 1 ? 'm' : 'ns'} selecionado${n === 1 ? '' : 's'}`;
}

export function openCompareModal(): void {
  const items = [...compareSelected]
    .map(id => (getTrip().cotacoes ?? {})[id])
    .filter(Boolean) as Cotacao[];
  if (items.length < 2) return;

  const fields = buildCompareFields(items);
  const cols   = items.length;

  openModal('Comparação de cotações', `
    <div class="compare-grid cols-${cols}">
      ${items.map((c, i) => `
        <div class="compare-card">
          <div class="compare-card-title">${sanitize(c.nome ?? 'Item ' + (i + 1))}</div>
          ${fields.map(f => `
            <div class="compare-field">
              <span class="compare-field-label">${f.label}</span>
              <span class="compare-field-val">${sanitize(f.get(c) ?? '—')}</span>
            </div>`).join('')}
        </div>`).join('')}
    </div>`, null);

  const saveBtn = document.getElementById('modal-save-btn');
  const cancelBtn = document.querySelector('.modal-btns .btn-g') as HTMLButtonElement | null;
  if (saveBtn) saveBtn.style.display = 'none';
  if (cancelBtn) cancelBtn.textContent = 'Fechar';
}

function buildCompareFields(items: Cotacao[]): { label: string; get: (c: Cotacao) => string }[] {
  const cat  = items[0]?.cat;
  const base = [
    { label: 'Categoria', get: (c: Cotacao) => CAT_LABELS[c.cat] ?? c.cat },
    { label: 'Preço',     get: (c: Cotacao) => c.preco ? fmtR(c.preco) : '—' },
    { label: 'Status',    get: (c: Cotacao) => c.status ?? '—' },
  ];
  const extra: { label: string; get: (c: Cotacao) => string }[] = [];
  if (cat === 'hotel') extra.push(
    { label: 'Cidade',    get: c => c.cidade ?? '' },
    { label: 'Check-in',  get: c => [fmt(c.checkin ?? ''), c.checkinTime].filter(Boolean).join(' às ') },
    { label: 'Check-out', get: c => [fmt(c.checkout ?? ''), c.checkoutTime].filter(Boolean).join(' às ') },
    { label: 'Noites',    get: c => c.noites ? String(c.noites) : '' },
    { label: 'Site',      get: c => c.site ?? '' },
    { label: 'Reserva',   get: c => c.reserva ?? '' },
  );
  if (cat === 'transporte') extra.push(
    { label: 'Tipo',      get: c => c.tipo ?? '' },
    { label: 'Rota',      get: c => [c.origem, c.destino].filter(Boolean).join(' → ') },
    { label: 'Partida',   get: c => [fmt(c.data ?? ''), c.horaPartida || c.hora].filter(Boolean).join(' às ') },
    { label: 'Chegada',   get: c => [c.dataChegada ? fmt(c.dataChegada) : '', c.horaChegada].filter(Boolean).join(' às ') },
    { label: 'Operadora', get: c => c.operadora ?? '' },
  );
  if (cat === 'ingresso') extra.push(
    { label: 'Local',     get: c => c.local ?? '' },
    { label: 'Data',      get: c => fmt(c.date ?? '') },
  );
  extra.push({ label: 'Notas', get: c => c.notas ?? '' });
  return [...base, ...extra];
}

export function buildCotacaoExtraFields(cat: CotacaoCat, p: (f: string) => string): string {
  if (cat === 'hotel') return `
    <div class="r2">
      <div class="fg"><label>Cidade</label><input id="f-cidade" value="${p('cidade')}"></div>
      <div class="fg"><label>Site de reserva</label><input id="f-site" value="${p('site')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Check-in</label><input type="date" id="f-cotacao-checkin" value="${p('checkin')}"></div>
      <div class="fg"><label>Check-out</label><input type="date" id="f-cotacao-checkout" value="${p('checkout')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Horário check-in</label><input type="time" id="f-checkin-time" value="${p('checkinTime')}"></div>
      <div class="fg"><label>Horário check-out</label><input type="time" id="f-checkout-time" value="${p('checkoutTime')}"></div>
    </div>
    <div class="fg"><label>Código / Reserva</label><input id="f-reserva" value="${p('reserva')}"></div>`;
  if (cat === 'transporte') return `
    <div class="r2">
      <div class="fg"><label>Tipo</label>
        <select id="f-tipo">
          ${TRANSPORT_TYPES.map(t => `<option value="${t}" ${p('tipo')===t?'selected':''}>${t}</option>`).join('')}
        </select>
      </div>
      <div class="fg"><label>Operadora</label><input id="f-operadora" value="${p('operadora')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Origem</label><input id="f-origem" value="${p('origem')}"></div>
      <div class="fg"><label>Destino</label><input id="f-destino" value="${p('destino')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Data da partida</label><input type="date" id="f-data" value="${p('data')}"></div>
      <div class="fg"><label>Data da chegada</label><input type="date" id="f-data-chegada" value="${p('dataChegada')}"></div>
    </div>
    <div class="r2">
      <div class="fg"><label>Hora da partida</label><input type="time" id="f-hora-partida" value="${p('horaPartida') || p('hora')}"></div>
      <div class="fg"><label>Hora da chegada</label><input type="time" id="f-hora-chegada" value="${p('horaChegada')}"></div>
    </div>`;
  if (cat === 'ingresso') return `
    <div class="r2">
      <div class="fg"><label>Local / Atração</label><input id="f-local" value="${p('local')}"></div>
      <div class="fg"><label>Data</label><input type="date" id="f-date" value="${p('date')}"></div>
    </div>`;
  return '';
}

export function updateCotacaoFields(): void {
  const cat = (document.getElementById('f-cotacao-cat') as HTMLSelectElement)?.value as CotacaoCat ?? 'hotel';
  const el  = document.getElementById('cotacao-extra-fields');
  if (!el) return;
  el.innerHTML = buildCotacaoExtraFields(cat, () => '');
}
